import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useCharacter } from '@/hooks/useCharacter';
import { useAuthStore } from '@/stores/auth';
import { getGymForPort, estimateStatGain } from '@/data/gyms';
import { NPCS } from '@/data/npcs';
import { TEXTURES } from '@/lib/assets';
import { formatCompact, formatNumber, formatStatGain } from '@/lib/utils';
type StatName = 'strength' | 'defense' | 'agility' | 'dexterity';

const STATS: { key: StatName; label: string; abbr: string; desc: string; glyph: string }[] = [
  { key: 'strength', label: 'Strength', abbr: 'STR', desc: 'Damage in combat', glyph: '⚔' },
  { key: 'defense', label: 'Defense', abbr: 'DEF', desc: 'Damage reduction', glyph: '◆' },
  { key: 'agility', label: 'Agility', abbr: 'AGI', desc: 'Attack speed and dodge', glyph: '✦' },
  {
    key: 'dexterity',
    label: 'Dexterity',
    abbr: 'DEX',
    desc: 'Hit chance and precision',
    glyph: '◎',
  },
];

const ENERGY_PRESETS = [10, 25, 50, 100] as const;

const BARTOLO_PORTS: Record<string, string> = {
  cutlass_cove: "Aye, fresh meat. Let's see if you've got the makings of a real pirate.",
  port_royale: "The Drill Hall's free. The Crown subsidizes physical fitness. Make use of it.",
  charleston: "You've come to train at the finest yard in the Caribbean. Show me you belong here.",
};

interface TrainingResult {
  stat: StatName;
  statGain: number;
  newStatValue: number;
  newEnergy: number;
  newDoubloons: number;
}

export default function TrainingPage(): JSX.Element {
  const navigate = useNavigate();
  const character = useCharacter();
  const refreshCharacter = useAuthStore((s) => s.refreshCharacter);

  const [selectedEnergy, setSelectedEnergy] = useState(25);
  const [training, setTraining] = useState(false);
  const [confirmStat, setConfirmStat] = useState<StatName | null>(null);
  const [result, setResult] = useState<TrainingResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const gym = character ? getGymForPort(character.current_port) : undefined;

  const statTotal = character
    ? character.strength + character.defense + character.agility + character.dexterity
    : 0;

  const handleTrain = useCallback(
    async (stat: StatName) => {
      if (!character || !gym) return;
      setTraining(true);
      setError(null);
      setConfirmStat(null);

      try {
        const { data, error: fnError } = await supabase.functions.invoke('training-attempt', {
          body: {
            characterId: character.id,
            stat,
            energyAmount: selectedEnergy,
            portId: gym.portId,
          },
        });

        if (fnError) throw fnError;
        if (data?.error) {
          const messages: Record<string, string> = {
            insufficient_energy: 'Not enough energy.',
            insufficient_doubloons: 'Not enough doubloons for the gym fee.',
            invalid_state: 'You cannot train right now.',
            rate_limited: 'Slow down. Try again in a moment.',
            forbidden: 'Character not found.',
          };
          setError(messages[data.error as string] ?? 'Training failed. Try again.');
          return;
        }

        setResult({
          stat,
          statGain: data.stat_gain,
          newStatValue: data.new_stat_value,
          newEnergy: data.new_energy,
          newDoubloons: data.new_doubloons,
        });

        await refreshCharacter();
      } catch {
        setError('Training failed. Check your connection.');
      } finally {
        setTraining(false);
      }
    },
    [character, gym, selectedEnergy, refreshCharacter],
  );

  if (!character) {
    return (
      <main className="min-h-dvh flex items-center justify-center">
        <p className="text-parchment-dark text-sm">Loading…</p>
      </main>
    );
  }

  if (!gym) {
    return (
      <main className="min-h-dvh flex items-center justify-center">
        <p className="text-parchment-dark text-sm">No gym available at this port.</p>
      </main>
    );
  }

  const bartoloGreeting = BARTOLO_PORTS[character.current_port.replace(/-/g, '_')];

  const effectiveEnergy = Math.min(selectedEnergy, character.energy);

  return (
    <div
      className="min-h-dvh"
      style={{
        backgroundImage: `url("${TEXTURES.wood}")`,
        backgroundSize: '600px',
        backgroundRepeat: 'repeat',
      }}
    >
      <div
        className="min-h-dvh"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(12,34,51,0.70) 0%, rgba(5,15,25,0.88) 70%)',
        }}
      >
        {/* Header */}
        <header className="relative overflow-hidden">
          <img
            src="/art/backgrounds/training_yard.webp"
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/40" />
          <div className="relative z-[5] mx-auto max-w-6xl px-4 sm:px-6 pt-14 sm:pt-20 pb-4 sm:pb-5">
            <button
              type="button"
              onClick={() => navigate('/game')}
              className="absolute top-3 left-3 sm:top-4 sm:left-5 z-10 inline-flex items-center gap-1.5 rounded-md border border-parchment/30 bg-black/40 backdrop-blur-sm px-3 py-2 text-xs font-display uppercase tracking-wider text-parchment-dark hover:border-candle hover:text-candle transition-colors min-h-11"
            >
              <span aria-hidden>&larr;</span>
              <span>Back</span>
            </button>

            <div className="flex items-end justify-between gap-4">
              <div>
                <h1 className="font-display text-2xl sm:text-3xl text-parchment drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                  {gym.name}
                </h1>
                <p className="text-xs sm:text-sm text-parchment-dark/80 mt-0.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                  {gym.dots} dots
                  {gym.cost > 0 && (
                    <>
                      <span className="mx-1.5 text-candle/50">&middot;</span>
                      {formatNumber(gym.cost)} doubloons per session
                    </>
                  )}
                </p>
              </div>
              <div className="text-right text-parchment drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                <div className="text-xs text-parchment-dark/80 uppercase tracking-wider">
                  Energy
                </div>
                <div className="font-mono text-lg">
                  {formatNumber(character.energy)}
                  <span className="text-parchment-dark/60">
                    {' '}
                    / {formatNumber(character.energy_max)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-3 sm:px-5 py-4 sm:py-6 space-y-4 sm:space-y-5 stage-fade-in">
          {/* Bartolo greeting */}
          {bartoloGreeting && (
            <div className="flex items-center gap-4 rounded-lg border-2 border-candle/40 bg-gradient-to-r from-sea-dark via-sea-deep to-sea-dark px-4 sm:px-5 py-3 shadow-md">
              <div className="shrink-0 h-14 w-14 sm:h-16 sm:w-16 rounded-full overflow-hidden border-2 border-candle/50 shadow-sm">
                <img
                  src={NPCS.drillmaster_bartolo.portrait}
                  alt="Drillmaster Bartolo"
                  className="h-full w-full object-cover"
                  loading="lazy"
                  width={64}
                  height={64}
                />
              </div>
              <div>
                <p className="font-display text-xs uppercase tracking-wider text-candle mb-0.5">
                  Drillmaster Bartolo
                </p>
                <p className="text-sm text-parchment-dark italic">
                  &ldquo;{bartoloGreeting}&rdquo;
                </p>
              </div>
            </div>
          )}

          {/* Energy selector */}
          <EnergySelector
            energy={character.energy}
            selected={selectedEnergy}
            onSelect={setSelectedEnergy}
          />

          {/* Error */}
          {error && (
            <div
              role="alert"
              className="rounded-md border-2 border-blood-bright/40 bg-blood-bright/10 px-3 py-2.5 text-sm text-blood shadow-sm"
            >
              {error}
            </div>
          )}

          {/* Result toast */}
          {result && <ResultBanner result={result} onDismiss={() => setResult(null)} />}

          {/* Stat cards */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {STATS.map((s) => (
              <StatCard
                key={s.key}
                stat={s}
                value={character[s.key]}
                training={training}
                recentResult={result?.stat === s.key ? result : null}
                onTrain={() => setConfirmStat(s.key)}
                disabled={character.energy < 1 || training}
              />
            ))}
          </div>
        </main>

        {/* Confirmation modal */}
        {confirmStat && (
          <ConfirmModal
            stat={STATS.find((s) => s.key === confirmStat)!.label}
            energy={effectiveEnergy}
            cost={gym.cost}
            doubloons={character.doubloons}
            estimate={estimateStatGain(gym.dots, effectiveEnergy, statTotal, character.happy)}
            onConfirm={() => {
              void handleTrain(confirmStat);
            }}
            onCancel={() => setConfirmStat(null)}
          />
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------

function EnergySelector({
  energy,
  selected,
  onSelect,
}: {
  energy: number;
  selected: number;
  onSelect: (v: number) => void;
}): JSX.Element {
  return (
    <div
      className="relative overflow-hidden rounded-lg border-2 border-wood-mid/70 shadow-md"
      style={{
        backgroundImage: `url("${TEXTURES.parchment}")`,
        backgroundSize: 'cover',
      }}
    >
      <div className="absolute inset-0 bg-parchment/80 pointer-events-none" />
      <div className="relative px-4 sm:px-5 py-3">
        <h2 className="font-display text-sm uppercase tracking-[0.2em] text-wood-dark mb-2">
          Energy to spend
        </h2>
        <div className="flex flex-wrap gap-2">
          {ENERGY_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              disabled={energy < preset}
              onClick={() => onSelect(preset)}
              className={`rounded-md border-2 px-3 py-2 text-sm font-mono min-h-11 transition-colors ${
                selected === preset && energy >= preset
                  ? 'border-candle bg-candle/20 text-ink'
                  : 'border-wood-mid/30 bg-parchment-light/50 text-ink-mid hover:border-candle/60 disabled:opacity-40 disabled:cursor-not-allowed'
              }`}
            >
              {preset}
            </button>
          ))}
          <button
            type="button"
            disabled={energy < 1}
            onClick={() => onSelect(energy)}
            className={`rounded-md border-2 px-3 py-2 text-sm font-mono min-h-11 transition-colors ${
              selected === energy
                ? 'border-candle bg-candle/20 text-ink'
                : 'border-wood-mid/30 bg-parchment-light/50 text-ink-mid hover:border-candle/60 disabled:opacity-40 disabled:cursor-not-allowed'
            }`}
          >
            Max ({energy})
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  stat,
  value,
  training,
  recentResult,
  onTrain,
  disabled,
}: {
  stat: (typeof STATS)[number];
  value: number;
  training: boolean;
  recentResult: TrainingResult | null;
  onTrain: () => void;
  disabled: boolean;
}): JSX.Element {
  const showGlow = recentResult !== null;
  return (
    <div
      className={`relative overflow-hidden rounded-lg border-2 shadow-md transition-all duration-500 ${
        showGlow ? 'border-candle/70 shadow-[0_0_20px_rgba(232,177,75,0.3)]' : 'border-wood-mid/70'
      }`}
      style={{
        backgroundImage: `url("${TEXTURES.parchment}")`,
        backgroundSize: 'cover',
      }}
    >
      <div className="absolute inset-0 bg-parchment/80 pointer-events-none" />
      <div className="relative px-3 sm:px-4 py-4 flex flex-col items-center text-center">
        <span aria-hidden className="text-candle-dark text-lg mb-1">
          {stat.glyph}
        </span>
        <h3 className="font-display text-sm sm:text-base uppercase tracking-[0.15em] text-wood-dark mb-1">
          {stat.label}
        </h3>
        <div
          className="font-mono text-2xl sm:text-3xl text-ink leading-none mb-1"
          title={formatNumber(Math.floor(value * 10) / 10)}
        >
          {formatCompact(value)}
        </div>
        <p className="text-[10px] sm:text-xs text-ink-light mb-3">{stat.desc}</p>

        {recentResult && (
          <div className="text-candle-dark font-display text-sm mb-2 stage-fade-in">
            +{formatStatGain(recentResult.statGain)} {stat.abbr}
          </div>
        )}

        <button
          type="button"
          onClick={onTrain}
          disabled={disabled}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-sea-deep text-candle border-2 border-candle/50 px-3 py-2 font-display tracking-[0.15em] uppercase text-xs shadow-sm hover:bg-sea-dark hover:border-candle transition-all min-h-11 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-candle"
        >
          {training ? (
            <span
              aria-hidden
              className="inline-block h-3 w-3 rounded-full border-2 border-candle/40 border-t-candle animate-spin"
            />
          ) : null}
          <span>Train {stat.abbr}</span>
        </button>
      </div>
    </div>
  );
}

function ConfirmModal({
  stat,
  energy,
  cost,
  doubloons,
  estimate,
  onConfirm,
  onCancel,
}: {
  stat: string;
  energy: number;
  cost: number;
  doubloons: number;
  estimate: number;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  const canAfford = doubloons >= cost;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm training"
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onCancel} />
      <div
        className="relative w-full max-w-sm rounded-lg border-2 border-wood-mid/70 shadow-[0_8px_32px_rgba(0,0,0,0.6)] stage-fade-in"
        style={{
          backgroundImage: `url("${TEXTURES.parchment}")`,
          backgroundSize: 'cover',
        }}
      >
        <div className="absolute inset-0 bg-parchment/85 pointer-events-none rounded-lg" />
        <div className="relative px-5 py-6 text-center">
          <h2 className="font-display text-xl text-wood-dark mb-3">Train {stat}?</h2>
          <div className="text-sm text-ink-mid space-y-1 mb-4">
            <p>
              Spend <strong className="text-ink font-mono">{energy}</strong> energy
              {cost > 0 && (
                <>
                  {' '}
                  and <strong className="text-ink font-mono">{formatNumber(cost)}</strong> doubloons
                </>
              )}
            </p>
            <p className="text-candle-dark">
              Estimated gain: ~{formatStatGain(estimate)} {stat}
            </p>
          </div>

          {!canAfford && (
            <p className="text-blood-bright text-xs mb-3">
              Not enough doubloons ({formatNumber(doubloons)} / {formatNumber(cost)})
            </p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 rounded-md border-2 border-wood-mid/40 bg-parchment-light/60 px-3 py-2.5 font-display text-xs uppercase tracking-wider text-ink-mid hover:border-wood-mid transition-colors min-h-11"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!canAfford}
              className="flex-1 rounded-md bg-sea-deep text-candle border-2 border-candle/50 px-3 py-2.5 font-display text-xs uppercase tracking-wider shadow-sm hover:bg-sea-dark hover:border-candle transition-all min-h-11 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-candle"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultBanner({
  result,
  onDismiss,
}: {
  result: TrainingResult;
  onDismiss: () => void;
}): JSX.Element {
  const statLabel = STATS.find((s) => s.key === result.stat)?.label ?? result.stat;
  return (
    <div
      role="status"
      className="rounded-lg border-2 border-candle/50 bg-candle/10 px-4 py-3 flex items-center justify-between gap-3 shadow-sm stage-fade-in"
    >
      <div className="text-sm text-ink">
        <span className="font-display text-candle-dark text-lg">
          +{formatStatGain(result.statGain)}
        </span>{' '}
        {statLabel}
        <span className="text-ink-light ml-2">→ {formatCompact(result.newStatValue)}</span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="text-ink-light hover:text-ink text-sm px-2 py-1"
        aria-label="Dismiss"
      >
        &times;
      </button>
    </div>
  );
}
